import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, CheckCircle2, XCircle, RefreshCw, Plus, Trash2, Globe, Target, Lightbulb, AlertCircle, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const priorityColors = { critical: 'border-red-200 bg-red-50 text-red-700', high: 'border-orange-200 bg-orange-50 text-orange-700', medium: 'border-blue-200 bg-blue-50 text-blue-700', low: 'border-gray-200 bg-gray-50 text-gray-600' };

export const LocalSEOScreen = () => {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [seoProfile, setSeoProfile] = useState(null);
  const [audit, setAudit] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newKwLocation, setNewKwLocation] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const bizRes = await api.get('/businesses');
      const businesses = bizRes.data.businesses || [];
      if (businesses.length === 0) { navigate('/more/business-profile/new'); return; }
      const biz = businesses[0];
      setBusiness(biz);
      const [seoRes, recRes] = await Promise.all([
        api.get(`/businesses/${biz.id}/local-seo`),
        api.get(`/businesses/${biz.id}/growth-recommendations`)
      ]);
      setSeoProfile(seoRes.data.seo_profile);
      if (seoRes.data.seo_profile?.last_audit_result) setAudit(seoRes.data.seo_profile.last_audit_result);
      setRecs((recRes.data.recommendations || []).filter(r => r.category === 'local_seo'));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRunAudit = async () => {
    if (!business || auditing) return;
    setAuditing(true);
    try {
      const res = await api.post(`/businesses/${business.id}/local-seo/audit`);
      setAudit(res.data.audit);
      toast.success('SEO audit completed');
      load();
    } catch { toast.error('Audit failed'); }
    setAuditing(false);
  };

  const handleAddKeyword = async () => {
    if (!business || !newKeyword.trim()) return;
    try {
      await api.post(`/businesses/${business.id}/local-seo/keywords`, { keyword: newKeyword.trim(), location: newKwLocation.trim() || null, intent: 'local', priority: 'medium' });
      setNewKeyword(''); setNewKwLocation('');
      toast.success('Keyword added');
      load();
    } catch { toast.error('Failed to add keyword'); }
  };

  const handleRemoveKeyword = async (kw) => {
    if (!business) return;
    try {
      await api.delete(`/businesses/${business.id}/local-seo/keywords/${encodeURIComponent(kw)}`);
      toast.success('Keyword removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const auditScore = audit?.score || 0;
  const keywords = seoProfile?.target_keywords || [];

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-[#128C7E] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="seo-back"><ChevronLeft size={24} /></button>
          <div className="flex-1"><h1 className="text-base font-bold">Local SEO</h1><p className="text-[10px] opacity-80">Improve how customers discover your business locally.</p></div>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-28 bg-gray-100 rounded-2xl" />
              <div className="h-12 bg-gray-100 rounded-xl" />
              <div className="h-32 bg-gray-100 rounded-xl" />
            </div>
          ) : (<>
          {/* SEO Score */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100" data-testid="seo-score-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Local SEO Score</p>
                <p className="text-3xl font-bold text-blue-800">{auditScore}<span className="text-sm font-normal text-blue-500">/100</span></p>
                <p className="text-xs text-blue-600 mt-1">{auditScore >= 70 ? 'Good — keep optimizing' : auditScore >= 40 ? 'Growing — room to improve' : 'Getting started'}</p>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                  <path className="text-blue-100" stroke="currentColor" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="text-blue-600" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray={`${auditScore}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
              </div>
            </div>
          </div>

          {/* Run Audit */}
          <button onClick={handleRunAudit} disabled={auditing}
            className="w-full h-12 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="run-audit-btn"
          >
            {auditing ? <><RefreshCw size={16} className="animate-spin" /> Running Audit...</> : <><Search size={16} /> Run SEO Audit</>}
          </button>

          {/* Audit Findings */}
          {audit && audit.findings && (
            <div className="space-y-2" data-testid="audit-findings">
              <h3 className="text-sm font-bold text-slate-700">SEO Audit Results</h3>
              <p className="text-xs text-gray-500">{audit.passed}/{audit.total_checks} checks passed</p>
              {['business_info', 'location', 'seo'].map(cat => {
                const catFindings = audit.findings.filter(f => f.category === cat);
                if (!catFindings.length) return null;
                return (
                  <div key={cat} className="bg-white border border-gray-200 rounded-xl p-3">
                    <h4 className="text-xs font-semibold text-slate-700 capitalize mb-2">{cat.replace('_', ' ')}</h4>
                    <div className="space-y-1.5">
                      {catFindings.map((f, i) => (
                        <div key={i} className="flex items-center gap-2">
                          {f.passed ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 flex-shrink-0" />}
                          <span className={`text-xs ${f.passed ? 'text-slate-700' : 'text-red-600'}`}>{f.check}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Keywords */}
          <div data-testid="keyword-section">
            <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1"><Target size={14} className="text-blue-600" /> Target Keywords</h3>
            <p className="text-[10px] text-gray-500 mb-3">Define your local keyword strategy. Keywords help you understand what customers search for.</p>

            {keywords.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {keywords.map((kw, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700">{kw.keyword}</p>
                      {kw.location && <p className="text-[10px] text-gray-500">{kw.location}</p>}
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize ${kw.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-blue-50 text-blue-600'}`}>{kw.priority}</span>
                    <button onClick={() => handleRemoveKeyword(kw.keyword)} className="p-1 text-red-400 hover:text-red-600" data-testid={`remove-kw-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} placeholder="e.g. designer sarees Delhi"
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" data-testid="keyword-input"
                onKeyDown={e => e.key === 'Enter' && handleAddKeyword()} />
              <button onClick={handleAddKeyword} className="px-3 py-2 bg-blue-600 text-white rounded-lg" data-testid="add-keyword-btn"><Plus size={16} /></button>
            </div>
            <input value={newKwLocation} onChange={e => setNewKwLocation(e.target.value)} placeholder="Location (optional, e.g. Jaipur)"
              className="w-full mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" data-testid="keyword-location-input" />
          </div>

          {/* Recommendations */}
          {recs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1"><Lightbulb size={14} className="text-amber-500" /> SEO Recommendations</h3>
              {recs.map((r, i) => (
                <button key={i} onClick={() => r.action_payload?.route && navigate(r.action_payload.route)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-2 text-left active:scale-[0.98] transition-transform ${priorityColors[r.priority] || priorityColors.medium}`}>
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">{r.title}</p>
                    <p className="text-[10px] opacity-70 mt-0.5">{r.description}</p>
                  </div>
                  <ChevronRight size={14} className="flex-shrink-0 opacity-50" />
                </button>
              ))}
            </div>
          )}

          {/* Google Business Card */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200" data-testid="google-biz-card">
            <div className="flex items-center gap-3 mb-3">
              <Globe size={20} className="text-gray-500" />
              <div>
                <p className="text-sm font-bold text-slate-700">Google Business Profile</p>
                <p className="text-[10px] text-gray-500">Not Connected</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-3">Connect your Google Business Profile to unlock real visibility insights, reviews, and local performance data.</p>
            <button className="w-full py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-500 flex items-center justify-center gap-1" disabled>
              Coming Soon
            </button>
          </div>
          </>)}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
